<template>
  <Modal
    :show="true"
    title=" "
    size="xs"
    transparentBackdrop
    :blurBackdrop="false"
    :closeOnBackdrop="true"
    :dialogStyle="{
      ...positionStyle,
      maxWidth: '360px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
    }"
    @close="closeModal"
  >
    <template #header>
      <div class="phone-header">
        <Icon icon="mdi:phone-settings" width="24" height="24" class="phone-header-icon" />
        <h6 class="phone-header-title">Edit phone number</h6>
      </div>
    </template>
    <template #body>
      <div class="phone-modal-body">
        <PhoneInput
          v-model="phoneNumber"
          defaultCountry="US"
          size="sm"
          label="Phone Number"
          :required="true"
          :error="phoneError"
        />
        <Select
          v-model="phoneNumberType"
          :options="phoneTypeOptions"
          label="Phone Type"
          :required="true"
          size="sm"
        />
      </div>
    </template>
    <template #footer>
      <Button type="button" variant="secondary" size="sm" @click="closeModal">Cancel</Button>
      <Button
        type="button"
        variant="primary"
        size="sm"
        :loading="isEditing"
        :disabled="isEditing"
        @click="onSubmit"
      >
        Save
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import { Button, Modal, PhoneInput, Select } from "@/components/ui";
import { useEditContactPhoneNumber } from "@/composables/contacts";
import { useToast } from "@/composables/ui/useToast";

const props = defineProps({
  contactId: { type: String, required: true },
  selectedNumber: { type: Object, required: true },
  anchorRect: { type: Object, default: null },
});

const positionStyle = computed(() => {
  if (!props.anchorRect) return undefined;
  const rect = props.anchorRect;
  const gap = 8;
  const dialogWidth = 360;
  let top = rect.bottom + gap;
  let right = window.innerWidth - rect.right;
  if (top + 320 > window.innerHeight) {
    top = Math.max(8, rect.top - 320 - gap);
  }
  if (right + dialogWidth > window.innerWidth - 8) {
    right = 8;
  }
  return { position: "fixed", top: `${top}px`, right: `${right}px`, left: "auto", margin: "0" };
});

const emit = defineEmits(["close-edit-phone-number-modal", "refresh-contact"]);

const $toast = useToast();

const phoneNumber = ref(props.selectedNumber.number);
const phoneNumberType = ref(props.selectedNumber.type);
const phoneError = ref("");

const phoneTypeOptions = [
  { label: "Mobile", value: "Mobile" },
  { label: "Direct", value: "Direct" },
  { label: "Work", value: "Work" },
  { label: "Home", value: "Home" },
  { label: "Other", value: "Other" },
];

const { editContactPhoneNumber, isEditing } = useEditContactPhoneNumber();

function mapPhoneTypeToApi(t) {
  const map = {
    Direct: "DIRECT",
    Mobile: "MOBILE",
    Work: "WORK",
    Home: "HOME",
    Other: "OTHER",
  };
  return map[t] || t;
}

function closeModal() {
  emit("close-edit-phone-number-modal");
}

async function onSubmit() {
  phoneError.value = "";

  if (!phoneNumber.value || !phoneNumber.value.trim()) {
    phoneError.value = "Phone number is required";
    return;
  }

  const payload = {
    contactId: props.contactId,
    phoneNumber: phoneNumber.value,
    oldPhoneNumber: props.selectedNumber.number,
    phoneNumberType: mapPhoneTypeToApi(phoneNumberType.value),
    oldPhoneNumberType: mapPhoneTypeToApi(props.selectedNumber.type),
  };

  try {
    const updatedContact = await editContactPhoneNumber(payload);
    if (updatedContact) {
      $toast.success("Phone number updated successfully");
      closeModal();
      emit("refresh-contact", updatedContact);
    }
  } catch (error) {
    $toast.error(error.message || "Failed to update phone number");
  }
}
</script>

<style scoped>
:deep(.ui-modal__content) {
  overflow: visible;
  position: relative;
}

:deep(.ui-modal__content)::before {
  content: "";
  position: absolute;
  top: -8px;
  right: 18px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-top: 1.5px solid #e0e0e0;
  border-left: 1.5px solid #e0e0e0;
  transform: rotate(45deg);
  z-index: 1;
  box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.04);
}

.phone-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.phone-header-icon {
  color: #3e3efe;
  flex-shrink: 0;
}

.phone-header-title {
  font-weight: 600;
  font-size: 15px;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.phone-modal-body {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
